import { Select, Spin } from "antd";
import type { FC } from "react";
import { useSearchParams } from "react-router";

import { useTranslation } from "src/translations";
import { getAssignedDataGroup } from "../../../../core/router/auth/SecurityManager";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { SelectInput } from "../../../Select/SelectInput";
import { useLoadConfigQuery } from "../../services/server/formComponentsApi";
import type { AntCustomFormField, SelectDynamicFormField } from "../../types/types";

interface DynamicSelectInputProps extends AntCustomFormField<string> {
    name: string;
    label: string;
    readOnly?: boolean;
    fieldConfig: SelectDynamicFormField;
    fetchOptionsOnSelectOnly?: boolean;
    filterOptions?: (option: Record<string, string>) => boolean;
    isSpinning?: boolean;
}

export const SelectDynamic: FC<DynamicSelectInputProps> = ({
    fieldConfig,
    readOnly,
    onChange,
    value,
    filterOptions,
    isSpinning = false,
    ...restProps
}) => {
    const {
        urlToFetchOptions,
        optionDisplayPropName = fieldConfig.optionValuePropName ? fieldConfig.optionValuePropName : "key",
        optionValuePropName = fieldConfig.optionValuePropName ? fieldConfig.optionValuePropName : "value",
        queryParams,
        useDataGroup,
    } = fieldConfig;
    const mustUseDataGroup = !!useDataGroup;

    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const params = {};

    let urlToFetch;
    if (mustUseDataGroup) {
        const dataGroup = getAssignedDataGroup();
        urlToFetch = `/${ResourceName.DATA_GROUPS}/${dataGroup?.code}${urlToFetchOptions}`;
    } else {
        urlToFetch = `${urlToFetchOptions}`;
    }

    if (queryParams) {
        queryParams.forEach((param) =>
            Object.keys(param).forEach((urlParam) => {
                // @ts-ignore
                const beParam = param[urlParam];
                // @ts-ignore
                params[beParam] = searchParams.get(urlParam);
            }),
        );
        const urlSearchParams = new URLSearchParams(params).toString();
        urlToFetch = `${urlToFetch}?${urlSearchParams}`;
    }

    const { data, isLoading } = useLoadConfigQuery(urlToFetch, { skip: !urlToFetch });

    const options = data?.items || [];
    const sortedOptions = [...options];
    if (sortedOptions.length > 1) {
        sortedOptions.sort((a, b) => {
            if (t(a[optionDisplayPropName]) === t(b[optionDisplayPropName])) {
                return 0;
            }
            return t(a[optionDisplayPropName]) > t(b[optionDisplayPropName]) ? 1 : -1;
        });
    }

    const onValueChange = (newValue: string) => {
        if (onChange) {
            onChange(newValue);
        }
    };

    // Apply filterOptions if provided
    const filteredOptions = filterOptions ? sortedOptions.filter(filterOptions) : sortedOptions;

    return (
        <Spin spinning={isSpinning}>
            <SelectInput
                onChange={onValueChange}
                value={value ? `${t(value)}` : undefined}
                isLoading={isLoading}
                disabled={readOnly}
                {...restProps}
            >
                {filteredOptions
                    .filter((option) => option.value)
                    .map((option: Record<string, string>) => {
                        return (
                            <Select.Option
                                key={option[optionValuePropName]}
                                value={option[optionValuePropName]}
                                displayName={t(option[optionDisplayPropName])}
                            >
                                {t(option[optionDisplayPropName])}
                            </Select.Option>
                        );
                    })}
            </SelectInput>
        </Spin>
    );
};
